/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * DataTable çıktısının kuyruğa alınması, izlenmesi ve güvenli aynı-origin indirilmesi.
 * Browser yalnız tablo görünümünü ürün API'sine yollar; Output ve DMS erişimi backend service token ile yapılır.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import axios from '../../api/axios';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Download,
  Eye,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  Loader2,
  Plus,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import {
  COMPANY_SCOPE_HEADER,
} from '../../config/company-scope.config';
import {
  NATIVE_LANGUAGE_NAMES,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '../../utils/language.utils';
import { useCompany } from '../../context/CompanyContext';
import { useTranslation } from '../../hooks/useTranslation';
import { isQubeProMobileWebView } from '../../utils/mobile-webview-navigation.utils';
import { FormBuilder, type FormField } from './FormBuilder';
import { FormModal } from './FormModal';
import {
  extractDataTableOutputApiData,
  normalizeDataTableOutputJob,
} from './data-table-output.utils';
export {
  buildDataTableOutputDataset,
  normalizeDataTableOutputJob,
} from './data-table-output.utils';

export type DataTableOutputFormat = 'PDF' | 'XLSX' | 'CSV';

export type DataTableOutputScope = 'VISIBLE_ROWS' | 'SELECTED_ROWS' | 'ALL_FILTERED_ROWS';
const DATA_TABLE_OUTPUT_FAMILY_CODE = 'generic-data-table';
const DATA_TABLE_OUTPUT_SYSTEM_TEMPLATE_CODE = 'qubepro-data-table-list-v2';
export type DataTableOutputColumnType =
  | 'TEXT'
  | 'CODE'
  | 'INTEGER'
  | 'DECIMAL'
  | 'CURRENCY'
  | 'PERCENT'
  | 'DATE'
  | 'DATETIME'
  | 'BOOLEAN';
export type DataTableOutputCell = string | number | boolean | null;

export interface DataTableOutputColumnFormat {
  currency?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  percentScale?: 1 | 100;
  dateStyle?: 'SHORT' | 'MEDIUM' | 'LONG';
}

export interface DataTableOutputColumn {
  key: string;
  label: string;
  type: DataTableOutputColumnType;
  align?: 'left' | 'center' | 'right';
  widthHint?: 'NARROW' | 'NORMAL' | 'WIDE';
  wrap?: boolean;
  keyColumn?: boolean;
  format?: DataTableOutputColumnFormat;
}

export type DataTableOutputExportStage =
  | 'PREPARING'
  | 'QUEUED'
  | 'RENDERING'
  | 'ARCHIVING'
  | 'READY'
  | 'FAILED';

export interface DataTableOutputExportJob {
  id: string | null;
  format: DataTableOutputFormat;
  status: 'PREPARING' | 'QUEUED' | 'RUNNING' | 'RETRYING' | 'SUCCESS' | 'FAILED' | 'DEAD' | 'CANCELLED';
  stage: DataTableOutputExportStage;
  fileName: string | null;
  recordCount: number;
  columnCount: number;
  redactedColumnCount: number;
  archived: boolean;
  errorMessage: string | null;
  createdAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  downloadUrl: string | null;
}

export interface DataTableOutputExportRequest {
  schemaVersion: 2;
  title?: string;
  timeZone: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  fileName?: string;
  tableId: string;
  columns: DataTableOutputColumn[];
  rows: DataTableOutputCell[][];
  scope: DataTableOutputScope;
  filters?: Array<{ label: string; value: string }>;
  quickSearch?: string;
  filterSummary?: string;
  sort?: { label: string; direction: 'asc' | 'desc' };
}

export interface UseDataTableOutputExportOptions extends DataTableOutputExportRequest {
  enabled: boolean;
  language: SupportedLanguage;
  /** Yetkili sistem (0) veya iş firması kapsamı; verilmezse seçili firma kullanılır. */
  companyScopeId?: string;
  allFilteredTemplatePolicy?: 'SYSTEM_MANAGED';
  defaultScope?: DataTableOutputScope;
  visibleRequest?: DataTableOutputExportRequest;
  selectedRequest?: DataTableOutputExportRequest;
  /** Read and validate row values only when the user requests an output. */
  prepareRequest?: (scope: DataTableOutputScope) => DataTableOutputExportRequest;
  hasSelectedRows?: boolean;
  columnCount?: number;
  queueAllFilteredOutput?: (selection: {
    format: DataTableOutputFormat;
    locale: SupportedLanguage;
    templateId: string;
    retry: boolean;
  }) => Promise<unknown>;
  canSetCompanyDefault?: boolean;
  canCloneTemplate?: boolean;
}

export interface DataTableOutputDataset {
  columns: DataTableOutputColumn[];
  rows: DataTableOutputCell[][];
}

interface DataTableOutputExportDialogProps {
  isOpen: boolean;
  job: DataTableOutputExportJob | null;
  language: SupportedLanguage;
  downloadError: string | null;
  isDownloading: boolean;
  isViewing: boolean;
  onClose: () => void;
  onDownload: () => void;
  onView: () => void;
  onRetry: () => void;
  onCreateNew: () => void;
}

type DataTableOutputCapabilityReasonCode =
  | 'FORMAT_DISABLED'
  | 'ENGINE_NOT_READY'
  | 'PAYLOAD_ENCRYPTION_UNAVAILABLE'
  | 'TEMPLATE_MISSING'
  | 'VARIANT_MISSING'
  | 'WORKER_STALE'
  | 'TENANT_NOT_ENROLLED'
  | 'DMS_UNAVAILABLE'
  | 'BRAND_UNAVAILABLE'
  | 'SCHEMA_MISMATCH';

interface DataTableOutputFormatOption {
  code: DataTableOutputFormat;
  mimeType: string;
  extension: string;
  engine: string;
  enabled: boolean;
  ready: boolean;
  available: boolean;
  reasonCode?: DataTableOutputCapabilityReasonCode;
}

interface DataTableOutputTemplateOption {
  id: string;
  code: string;
  name: string;
  ownership: 'SYSTEM' | 'COMPANY';
  status: 'ACTIVE';
  version: number;
  designContractVersion: number;
  designChecksumSha256: string;
  formats: DataTableOutputFormat[];
}

interface DataTableOutputOptionsResponse {
  contractVersion: 1;
  familyCode: 'generic-data-table';
  locale: SupportedLanguage;
  availableLocales: SupportedLanguage[];
  formats: DataTableOutputFormatOption[];
  templates: DataTableOutputTemplateOption[];
  defaults: Record<DataTableOutputFormat, {
    templateId: string | null;
    templateCode: string | null;
    source: 'USER' | 'COMPANY' | 'SYSTEM';
  }>;
}

interface DataTableCorporateOutputDialogProps {
  isOpen: boolean;
  language: SupportedLanguage;
  loading: boolean;
  submitting: boolean;
  options: DataTableOutputOptionsResponse | null;
  loadError: string | null;
  scope: DataTableOutputScope;
  format: DataTableOutputFormat;
  locale: SupportedLanguage;
  templateId: string;
  saveUserDefault: boolean;
  saveCompanyDefault: boolean;
  selectedRowsAvailable: boolean;
  allFilteredAvailable: boolean;
  canSetCompanyDefault: boolean;
  canCloneTemplate: boolean;
  designCatalogUrl: string;
  returnedDesignPending: boolean;
  systemManagedAllFiltered: boolean;
  onClose: () => void;
  onRefresh: () => void;
  onScopeChange: (scope: DataTableOutputScope) => void;
  onFormatChange: (format: DataTableOutputFormat) => void;
  onLocaleChange: (locale: SupportedLanguage) => void;
  onTemplateChange: (templateId: string) => void;
  onSaveUserDefaultChange: (value: boolean) => void;
  onSaveCompanyDefaultChange: (value: boolean) => void;
  onSubmit: () => void;
  onDesignCatalogOpen: () => void;
}

interface OutputExportCopy {
  title: string;
  preparing: string;
  generating: string;
  archiving: string;
  ready: string;
  failedTitle: string;
  readyTitle: string;
  queuedTitle: string;
  failedMessage: string;
  readyMessage: string;
  activeMessage: string;
  close: string;
  retry: string;
  newFile: string;
  download: string;
  downloading: string;
  view: string;
  viewing: string;
  file: string;
  records: string;
  columns: string;
  jobId: string;
  started: string;
  duration: string;
  beingGenerated: string;
  sensitiveExcluded: string;
  dmsFolder: string;
  downloadError: string;
  viewError: string;
  startError: string;
  stagesAria: string;
}

const OUTPUT_EXPORT_COPY: Record<SupportedLanguage, OutputExportCopy> = {
  tr: {
    title: '{format} dışa aktarımı', preparing: 'Veriler hazırlanıyor', generating: '{format} oluşturuluyor', archiving: 'DMS arşivine kaydediliyor', ready: 'Hazır',
    failedTitle: '{format} oluşturulamadı', readyTitle: '{format} hazır', queuedTitle: 'Kuyruğa alındı', failedMessage: 'Üretim işi tamamlanamadı.',
    readyMessage: 'Dosya güvenli bağlantı üzerinden indirilebilir.', activeMessage: 'İş arka planda devam ediyor. Bu pencereyi kapatabilirsiniz.',
    close: 'Kapat', retry: 'Tekrar dene', newFile: 'Yeni {format}', download: '{format} indir', downloading: 'İndiriliyor', view: '{format} görüntüle', viewing: 'Açılıyor',
    file: 'Dosya', records: 'Kayıt', columns: 'Sütun', jobId: 'İş no', started: 'Başlangıç', duration: 'Süre', beingGenerated: 'Oluşturuluyor',
    sensitiveExcluded: '{count} hassas sütun güvenlik nedeniyle çıktıya eklenmedi.', dmsFolder: 'DMS klasörü',
    downloadError: 'Dosya indirilemedi. Lütfen tekrar deneyin.', viewError: 'PDF görüntülenemedi. Açılır pencere iznini kontrol edip tekrar deneyin.', startError: 'Çıktı işi başlatılamadı.', stagesAria: 'Çıktı üretim aşamaları',
  },
  en: {
    title: '{format} export', preparing: 'Preparing data', generating: 'Generating {format}', archiving: 'Archiving in DMS', ready: 'Ready',
    failedTitle: '{format} could not be generated', readyTitle: '{format} is ready', queuedTitle: 'Queued', failedMessage: 'The generation job could not be completed.',
    readyMessage: 'The file can be downloaded through the secure link.', activeMessage: 'The job continues in the background. You can close this window.',
    close: 'Close', retry: 'Retry', newFile: 'New {format}', download: 'Download {format}', downloading: 'Downloading', view: 'View {format}', viewing: 'Opening',
    file: 'File', records: 'Records', columns: 'Columns', jobId: 'Job ID', started: 'Started', duration: 'Duration', beingGenerated: 'Being generated',
    sensitiveExcluded: '{count} sensitive column(s) were excluded for security.', dmsFolder: 'DMS folder',
    downloadError: 'The file could not be downloaded. Please try again.', viewError: 'The PDF could not be opened. Check the pop-up permission and try again.', startError: 'The output job could not be started.', stagesAria: 'Output generation stages',
  },
  ar: {
    title: 'تصدير {format}', preparing: 'جارٍ إعداد البيانات', generating: 'جارٍ إنشاء {format}', archiving: 'جارٍ الحفظ في DMS', ready: 'جاهز',
    failedTitle: 'تعذر إنشاء {format}', readyTitle: '{format} جاهز', queuedTitle: 'تمت الإضافة إلى قائمة الانتظار', failedMessage: 'تعذر إكمال مهمة الإنشاء.',
    readyMessage: 'يمكن تنزيل الملف عبر الرابط الآمن.', activeMessage: 'تستمر المهمة في الخلفية. يمكنك إغلاق هذه النافذة.',
    close: 'إغلاق', retry: 'إعادة المحاولة', newFile: '{format} جديد', download: 'تنزيل {format}', downloading: 'جارٍ التنزيل', view: 'عرض {format}', viewing: 'جارٍ الفتح',
    file: 'الملف', records: 'السجلات', columns: 'الأعمدة', jobId: 'رقم المهمة', started: 'البدء', duration: 'المدة', beingGenerated: 'جارٍ الإنشاء',
    sensitiveExcluded: 'تم استبعاد {count} من الأعمدة الحساسة لأسباب أمنية.', dmsFolder: 'مجلد DMS',
    downloadError: 'تعذر تنزيل الملف. حاول مرة أخرى.', viewError: 'تعذر فتح ملف PDF. تحقق من إذن النوافذ المنبثقة وحاول مرة أخرى.', startError: 'تعذر بدء مهمة الإخراج.', stagesAria: 'مراحل إنشاء الإخراج',
  },
  de: {
    title: '{format}-Export', preparing: 'Daten werden vorbereitet', generating: '{format} wird erstellt', archiving: 'Wird in DMS archiviert', ready: 'Bereit',
    failedTitle: '{format} konnte nicht erstellt werden', readyTitle: '{format} ist bereit', queuedTitle: 'In Warteschlange', failedMessage: 'Der Erstellungsauftrag konnte nicht abgeschlossen werden.',
    readyMessage: 'Die Datei kann über den sicheren Link heruntergeladen werden.', activeMessage: 'Der Auftrag läuft im Hintergrund weiter. Sie können dieses Fenster schließen.',
    close: 'Schließen', retry: 'Erneut versuchen', newFile: 'Neue {format}', download: '{format} herunterladen', downloading: 'Wird heruntergeladen', view: '{format} anzeigen', viewing: 'Wird geöffnet',
    file: 'Datei', records: 'Datensätze', columns: 'Spalten', jobId: 'Auftrags-ID', started: 'Gestartet', duration: 'Dauer', beingGenerated: 'Wird erstellt',
    sensitiveExcluded: '{count} sensible Spalte(n) wurden aus Sicherheitsgründen ausgeschlossen.', dmsFolder: 'DMS-Ordner',
    downloadError: 'Die Datei konnte nicht heruntergeladen werden. Bitte versuchen Sie es erneut.', viewError: 'Die PDF-Datei konnte nicht geöffnet werden. Prüfen Sie die Pop-up-Berechtigung und versuchen Sie es erneut.', startError: 'Der Ausgabeauftrag konnte nicht gestartet werden.', stagesAria: 'Ausgabeschritte',
  },
  fr: {
    title: 'Export {format}', preparing: 'Préparation des données', generating: 'Création de {format}', archiving: 'Archivage dans DMS', ready: 'Prêt',
    failedTitle: 'Impossible de créer {format}', readyTitle: '{format} est prêt', queuedTitle: 'Mis en file', failedMessage: 'La tâche de génération n’a pas pu être terminée.',
    readyMessage: 'Le fichier peut être téléchargé via le lien sécurisé.', activeMessage: 'La tâche continue en arrière-plan. Vous pouvez fermer cette fenêtre.',
    close: 'Fermer', retry: 'Réessayer', newFile: 'Nouveau {format}', download: 'Télécharger {format}', downloading: 'Téléchargement', view: 'Afficher {format}', viewing: 'Ouverture',
    file: 'Fichier', records: 'Enregistrements', columns: 'Colonnes', jobId: 'ID de tâche', started: 'Début', duration: 'Durée', beingGenerated: 'En cours de création',
    sensitiveExcluded: '{count} colonne(s) sensible(s) ont été exclues pour des raisons de sécurité.', dmsFolder: 'Dossier DMS',
    downloadError: 'Le fichier n’a pas pu être téléchargé. Veuillez réessayer.', viewError: 'Le PDF n’a pas pu être ouvert. Vérifiez l’autorisation des fenêtres contextuelles et réessayez.', startError: 'La tâche de sortie n’a pas pu démarrer.', stagesAria: 'Étapes de génération de sortie',
  },
  es: {
    title: 'Exportación {format}', preparing: 'Preparando datos', generating: 'Generando {format}', archiving: 'Archivando en DMS', ready: 'Listo',
    failedTitle: 'No se pudo generar {format}', readyTitle: '{format} está listo', queuedTitle: 'En cola', failedMessage: 'No se pudo completar el trabajo de generación.',
    readyMessage: 'El archivo puede descargarse mediante el enlace seguro.', activeMessage: 'El trabajo continúa en segundo plano. Puede cerrar esta ventana.',
    close: 'Cerrar', retry: 'Reintentar', newFile: 'Nuevo {format}', download: 'Descargar {format}', downloading: 'Descargando', view: 'Ver {format}', viewing: 'Abriendo',
    file: 'Archivo', records: 'Registros', columns: 'Columnas', jobId: 'ID de trabajo', started: 'Inicio', duration: 'Duración', beingGenerated: 'Generando',
    sensitiveExcluded: 'Se excluyeron {count} columna(s) sensible(s) por seguridad.', dmsFolder: 'Carpeta DMS',
    downloadError: 'No se pudo descargar el archivo. Inténtelo de nuevo.', viewError: 'No se pudo abrir el PDF. Compruebe el permiso de ventanas emergentes e inténtelo de nuevo.', startError: 'No se pudo iniciar el trabajo de salida.', stagesAria: 'Etapas de generación de salida',
  },
  it: {
    title: 'Esportazione {format}', preparing: 'Preparazione dati', generating: 'Creazione di {format}', archiving: 'Archiviazione in DMS', ready: 'Pronto',
    failedTitle: 'Impossibile creare {format}', readyTitle: '{format} è pronto', queuedTitle: 'In coda', failedMessage: 'Il processo di generazione non è stato completato.',
    readyMessage: 'Il file può essere scaricato tramite il collegamento sicuro.', activeMessage: 'Il processo continua in background. Puoi chiudere questa finestra.',
    close: 'Chiudi', retry: 'Riprova', newFile: 'Nuovo {format}', download: 'Scarica {format}', downloading: 'Download in corso', view: 'Visualizza {format}', viewing: 'Apertura',
    file: 'File', records: 'Record', columns: 'Colonne', jobId: 'ID processo', started: 'Avvio', duration: 'Durata', beingGenerated: 'In creazione',
    sensitiveExcluded: '{count} colonna/e sensibile/i sono state escluse per sicurezza.', dmsFolder: 'Cartella DMS',
    downloadError: 'Impossibile scaricare il file. Riprova.', viewError: 'Impossibile aprire il PDF. Controlla il permesso per i popup e riprova.', startError: 'Impossibile avviare il processo di output.', stagesAria: 'Fasi di generazione output',
  },
  ru: {
    title: 'Экспорт {format}', preparing: 'Подготовка данных', generating: 'Создание {format}', archiving: 'Архивация в DMS', ready: 'Готово',
    failedTitle: 'Не удалось создать {format}', readyTitle: '{format} готов', queuedTitle: 'В очереди', failedMessage: 'Не удалось завершить задание создания.',
    readyMessage: 'Файл можно скачать по защищённой ссылке.', activeMessage: 'Задание выполняется в фоне. Это окно можно закрыть.',
    close: 'Закрыть', retry: 'Повторить', newFile: 'Новый {format}', download: 'Скачать {format}', downloading: 'Скачивание', view: 'Просмотреть {format}', viewing: 'Открытие',
    file: 'Файл', records: 'Записи', columns: 'Столбцы', jobId: 'ID задания', started: 'Начало', duration: 'Длительность', beingGenerated: 'Создаётся',
    sensitiveExcluded: 'Конфиденциальных столбцов исключено: {count}.', dmsFolder: 'Папка DMS',
    downloadError: 'Не удалось скачать файл. Повторите попытку.', viewError: 'Не удалось открыть PDF. Проверьте разрешение всплывающих окон и повторите попытку.', startError: 'Не удалось запустить задание вывода.', stagesAria: 'Этапы создания вывода',
  },
  zh: {
    title: '导出 {format}', preparing: '正在准备数据', generating: '正在生成 {format}', archiving: '正在归档到 DMS', ready: '已就绪',
    failedTitle: '无法生成 {format}', readyTitle: '{format} 已就绪', queuedTitle: '已排队', failedMessage: '生成任务未能完成。',
    readyMessage: '可通过安全链接下载文件。', activeMessage: '任务正在后台继续。您可以关闭此窗口。',
    close: '关闭', retry: '重试', newFile: '新建 {format}', download: '下载 {format}', downloading: '正在下载', view: '查看 {format}', viewing: '正在打开',
    file: '文件', records: '记录', columns: '列', jobId: '任务 ID', started: '开始时间', duration: '用时', beingGenerated: '正在生成',
    sensitiveExcluded: '出于安全原因，已排除 {count} 个敏感列。', dmsFolder: 'DMS 文件夹',
    downloadError: '无法下载文件，请重试。', viewError: '无法打开 PDF。请检查弹出窗口权限后重试。', startError: '无法启动输出任务。', stagesAria: '输出生成阶段',
  },
  ja: {
    title: '{format} エクスポート', preparing: 'データを準備中', generating: '{format} を生成中', archiving: 'DMS に保存中', ready: '準備完了',
    failedTitle: '{format} を生成できませんでした', readyTitle: '{format} の準備ができました', queuedTitle: 'キューに追加済み', failedMessage: '生成ジョブを完了できませんでした。',
    readyMessage: '安全なリンクからファイルをダウンロードできます。', activeMessage: 'ジョブはバックグラウンドで続行中です。この画面を閉じても問題ありません。',
    close: '閉じる', retry: '再試行', newFile: '新しい {format}', download: '{format} をダウンロード', downloading: 'ダウンロード中', view: '{format} を表示', viewing: '開いています',
    file: 'ファイル', records: 'レコード', columns: '列', jobId: 'ジョブ ID', started: '開始', duration: '所要時間', beingGenerated: '生成中',
    sensitiveExcluded: 'セキュリティ上の理由で {count} 個の機密列を除外しました。', dmsFolder: 'DMS フォルダー',
    downloadError: 'ファイルをダウンロードできませんでした。もう一度お試しください。', viewError: 'PDF を開けませんでした。ポップアップの許可を確認して、もう一度お試しください。', startError: '出力ジョブを開始できませんでした。', stagesAria: '出力生成ステージ',
  },
};

const OUTPUT_MIME_TYPES: Record<DataTableOutputFormat, string> = {
  PDF: 'application/pdf',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  CSV: 'text/csv',
};

const OUTPUT_EXTENSIONS: Record<DataTableOutputFormat, string> = {
  PDF: 'pdf',
  XLSX: 'xlsx',
  CSV: 'csv',
};

function copyFor(language: SupportedLanguage): OutputExportCopy {
  return OUTPUT_EXPORT_COPY[language] ?? OUTPUT_EXPORT_COPY.en;
}

function interpolate(value: string, replacements: Record<string, string | number>): string {
  return Object.entries(replacements).reduce(
    (result, [key, replacement]) => result.split(`{${key}}`).join(String(replacement)),
    value,
  );
}

function pageTitle(fallback?: string): string | undefined {
  if (fallback?.trim()) return fallback.trim();
  if (typeof document === 'undefined') return undefined;
  const heading = document.querySelector('.main-content h1, .page-container h1, main h1');
  const text = heading?.textContent?.trim();
  if (text) return text.slice(0, 200);
  return document.title?.split('|')[0]?.trim().slice(0, 200) || undefined;
}

function pagePath(): string {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname || '/'}${window.location.search || ''}`.slice(0, 240);
}

function apiErrorMessage(error: unknown, fallback: string): string {
  if (!axios.isAxiosError(error)) return error instanceof Error && error.message ? error.message : fallback;
  const data = error.response?.data;
  if (data && typeof data === 'object' && !(data instanceof ArrayBuffer)) {
    const payload = data as { error?: unknown; message?: unknown };
    const message = payload.error ?? payload.message;
    if (message) return String(message);
  }
  if (typeof error.message === 'string' && error.message.trim()) return error.message;
  return fallback;
}

const OUTPUT_REASON_COPY: Record<DataTableOutputCapabilityReasonCode, { tr: string; en: string }> = {
  FORMAT_DISABLED: { tr: 'Bu format DataTable ailesi için etkin değil.', en: 'This format is not enabled for the DataTable family.' },
  ENGINE_NOT_READY: { tr: 'Format motoru henüz hazır değil.', en: 'The format engine is not ready.' },
  PAYLOAD_ENCRYPTION_UNAVAILABLE: { tr: 'Güvenli kuyruk şifreleme servisi hazır değil.', en: 'The secure queue encryption service is unavailable.' },
  TEMPLATE_MISSING: { tr: 'Yayınlanmış sistem tasarımı bulunamadı.', en: 'No published system design is available.' },
  VARIANT_MISSING: { tr: 'Bu dil ve format için şablon varyantı eksik.', en: 'The template variant is missing for this locale and format.' },
  WORKER_STALE: { tr: 'Output Worker güncel heartbeat vermiyor.', en: 'The Output Worker heartbeat is stale.' },
  TENANT_NOT_ENROLLED: { tr: 'Bu tenant Output Worker kapsamına alınmamış.', en: 'This tenant is not enrolled for the Output Worker.' },
  DMS_UNAVAILABLE: { tr: 'DMS arşivleme servisi hazır değil.', en: 'The DMS archive service is unavailable.' },
  BRAND_UNAVAILABLE: { tr: 'Kurumsal marka profili çözümlenemedi.', en: 'The corporate brand profile is unavailable.' },
  SCHEMA_MISMATCH: { tr: 'Şablon ve snapshot sözleşmesi uyuşmuyor.', en: 'The template and snapshot contracts do not match.' },
};

function outputReasonText(code: DataTableOutputCapabilityReasonCode | undefined, language: SupportedLanguage): string {
  if (!code) return '';
  const copy = OUTPUT_REASON_COPY[code];
  if (!copy) return language === 'tr' ? 'Çıktı servisi hazır değil.' : 'The output service is unavailable.';
  return language === 'tr' ? copy.tr : copy.en;
}

function normalizeOutputOptions(value: unknown): DataTableOutputOptionsResponse {
  const raw = value && typeof value === 'object' ? value as Partial<DataTableOutputOptionsResponse> : {};
  if (raw.contractVersion !== 1 || raw.familyCode !== 'generic-data-table' || !Array.isArray(raw.formats) || !Array.isArray(raw.templates)) {
    throw new Error('Output options contract is invalid.');
  }
  return raw as DataTableOutputOptionsResponse;
}

function outputDesignCatalogUrl(): string {
  if (typeof window === 'undefined') {
    return `/output/templates?familyCode=${DATA_TABLE_OUTPUT_FAMILY_CODE}&originSystemTemplateCode=${DATA_TABLE_OUTPUT_SYSTEM_TEMPLATE_CODE}`;
  }
  const configuredBase = String(
    import.meta.env.VITE_PLATFORM_URL || import.meta.env.VITE_BASE_URL || '',
  ).trim();
  const base = /^https?:\/\//iu.test(configuredBase) ? configuredBase : window.location.origin;
  const url = new URL('/output/templates', base);
  url.searchParams.set('familyCode', DATA_TABLE_OUTPUT_FAMILY_CODE);
  url.searchParams.set('originSystemTemplateCode', DATA_TABLE_OUTPUT_SYSTEM_TEMPLATE_CODE);
  return url.toString();
}

function responseFileName(contentDisposition: unknown, fallback: string): string {
  const header = String(contentDisposition || '');
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }
  return header.match(/filename="?([^";]+)"?/i)?.[1] || fallback;
}

function validateOutputResponse(data: unknown, contentType: unknown, format: DataTableOutputFormat): ArrayBuffer {
  const buffer = data instanceof ArrayBuffer
    ? data
    : ArrayBuffer.isView(data)
      ? data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer
      : new ArrayBuffer(0);
  const bytes = new Uint8Array(buffer);
  const mimeType = String(contentType || '').split(';')[0].trim().toLowerCase();
  const expectedMimeType = OUTPUT_MIME_TYPES[format].toLowerCase();
  const validSignature = format === 'PDF'
    ? bytes.length >= 5 && String.fromCharCode(...bytes.slice(0, 5)) === '%PDF-'
    : format === 'XLSX'
      ? bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04
      : bytes.length > 0;
  if (mimeType !== expectedMimeType || !validSignature) {
    throw new Error(`Server did not return a valid ${format} file.`);
  }
  return buffer;
}

export function useDataTableOutputExport(options: UseDataTableOutputExportOptions) {
  const { selectedCompanyId } = useCompany();
  const outputCompanyScopeId = options.companyScopeId?.trim() || selectedCompanyId;
  const [isOpen, setIsOpen] = useState(false);
  const [configurationOpen, setConfigurationOpen] = useState(false);
  const [job, setJob] = useState<DataTableOutputExportJob | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [readyFormats, setReadyFormats] = useState<DataTableOutputFormat[]>([]);
  const [readyCompanyScopeId, setReadyCompanyScopeId] = useState<string | null>(null);
  const [outputOptions, setOutputOptions] = useState<DataTableOutputOptionsResponse | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsLoadError, setOptionsLoadError] = useState<string | null>(null);
  const [configurationActionBusy, setConfigurationActionBusy] = useState(false);
  const [optionsRefreshKey, setOptionsRefreshKey] = useState(0);
  const [configurationScope, setConfigurationScope] = useState<DataTableOutputScope>(
    options.defaultScope === 'ALL_FILTERED_ROWS' && options.queueAllFilteredOutput
      ? 'ALL_FILTERED_ROWS'
      : options.hasSelectedRows || options.selectedRequest ? 'SELECTED_ROWS' : 'VISIBLE_ROWS',
  );
  const [configurationFormat, setConfigurationFormat] = useState<DataTableOutputFormat>('PDF');
  const [configurationLocale, setConfigurationLocale] = useState<SupportedLanguage>(options.language);
  const [configurationTemplateId, setConfigurationTemplateId] = useState('');
  const [configurationSelectedRequest, setConfigurationSelectedRequest] = useState<DataTableOutputExportRequest | null>(null);
  const [saveUserDefault, setSaveUserDefault] = useState(false);
  const [saveCompanyDefault, setSaveCompanyDefault] = useState(false);
  const [returnedTemplateId, setReturnedTemplateId] = useState('');
  const [returnedDesignPending, setReturnedDesignPending] = useState(false);
  const refreshDesignCatalogOnFocusRef = useRef(false);
  const companyScopeRef = useRef('');
  const lastRequestRef = useRef<DataTableOutputExportRequest | null>(null);
  const lastSelectionRef = useRef<{ locale: SupportedLanguage; templateId: string } | null>(null);
  const lastServerPreparedRef = useRef<{
    format: DataTableOutputFormat;
    createJob: (retry: boolean) => Promise<unknown>;
  } | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    companyScopeRef.current = outputCompanyScopeId;
    lastRequestRef.current = null;
    lastSelectionRef.current = null;
    lastServerPreparedRef.current = null;
    setJob(null);
    setIsOpen(false);
    setDownloadError(null);
    setReadyFormats([]);
    setReadyCompanyScopeId(null);
    setOutputOptions(null);
    setOptionsLoadError(null);
    setConfigurationOpen(false);
    setConfigurationSelectedRequest(null);
    setReturnedTemplateId('');
    setReturnedDesignPending(false);
  }, [outputCompanyScopeId]);

  useEffect(() => {
    if (!options.enabled || typeof window === 'undefined') return;
    const query = new URLSearchParams(window.location.search || '');
    if (query.get('qpOutputReturn') !== '1' || query.get('qpOutputTable') !== options.tableId) return;
    const templateId = String(query.get('outputDesignTemplateId') || '').trim();
    if (/^\d+$/u.test(templateId)) setReturnedTemplateId(templateId);
    setReturnedDesignPending(query.get('outputDesignState') !== 'PUBLISHED');
    setConfigurationOpen(true);
    setOptionsRefreshKey((value) => value + 1);
    ['qpOutputReturn', 'qpOutputTable', 'outputDesignTemplateId', 'outputDesignState'].forEach((key) => query.delete(key));
    const cleanQuery = query.toString();
    window.history.replaceState(
      window.history.state,
      document.title,
      `${window.location.pathname}${cleanQuery ? `?${cleanQuery}` : ''}${window.location.hash || ''}`,
    );
  }, [options.enabled, options.tableId]);

  useEffect(() => {
    setConfigurationLocale(options.language);
  }, [options.language]);

  useEffect(() => {
    let cancelled = false;
    setOptionsLoading(true);
    setOptionsLoadError(null);
    const companyId = outputCompanyScopeId.trim();
    if (!options.enabled || !configurationOpen || !companyId) {
      setOptionsLoading(false);
      return () => { cancelled = true; };
    }
    void axios.get('/api/output-exports/options', {
      params: { locale: configurationLocale },
      headers: { [COMPANY_SCOPE_HEADER]: companyId },
    }).then((response) => {
      if (cancelled) return;
      const resolved = normalizeOutputOptions(extractDataTableOutputApiData(response.data));
      const formats = resolved.formats.filter((format) => format.available).map((format) => format.code);
      const declared = resolved.formats.map((format) => format.code);
      const preferred = formats.includes('PDF') ? 'PDF' : formats[0] ?? declared[0] ?? 'PDF';
      setOutputOptions(resolved);
      setReadyFormats(formats);
      setReadyCompanyScopeId(companyId);
      setConfigurationFormat(preferred);
    }).catch((error) => {
      if (!cancelled) {
        setReadyFormats([]);
        setReadyCompanyScopeId(companyId);
        setOutputOptions(null);
        setOptionsLoadError(apiErrorMessage(error, copyFor(options.language).startError));
      }
    }).finally(() => {
      if (!cancelled) setOptionsLoading(false);
    });
    return () => { cancelled = true; };
  }, [configurationLocale, configurationOpen, options.enabled, options.language, optionsRefreshKey, outputCompanyScopeId]);

  useEffect(() => {
    const candidates = outputOptions?.templates.filter((template) => template.formats.includes(configurationFormat)) || [];
    const currentIsValid = candidates.some((template) => template.id === configurationTemplateId);
    if (currentIsValid) return;
    const resolvedDefault = outputOptions?.defaults?.[configurationFormat]?.templateId;
    const returned = candidates.find((template) => template.id === returnedTemplateId);
    const next = returned ?? candidates.find((template) => template.id === resolvedDefault) ?? candidates[0];
    setConfigurationTemplateId(next?.id || '');
    if (returned) {
      setReturnedTemplateId('');
      setReturnedDesignPending(false);
    }
  }, [configurationFormat, configurationTemplateId, outputOptions, returnedTemplateId]);

  useEffect(() => {
    if (configurationScope !== 'ALL_FILTERED_ROWS' || options.allFilteredTemplatePolicy !== 'SYSTEM_MANAGED') return;
    const systemTemplate = outputOptions?.templates.find((template) => (
      template.ownership === 'SYSTEM' && template.formats.includes(configurationFormat)
    ));
    if (systemTemplate && systemTemplate.id !== configurationTemplateId) {
      setConfigurationTemplateId(systemTemplate.id);
      setSaveUserDefault(false);
      setSaveCompanyDefault(false);
    }
  }, [configurationFormat, configurationScope, configurationTemplateId, options.allFilteredTemplatePolicy, outputOptions]);

  useEffect(() => {
    if (!configurationOpen || typeof window === 'undefined') return undefined;
    const refreshPublishedDesigns = () => {
      if (!refreshDesignCatalogOnFocusRef.current) return;
      refreshDesignCatalogOnFocusRef.current = false;
      setOptionsRefreshKey((value) => value + 1);
    };
    window.addEventListener('focus', refreshPublishedDesigns);
    return () => window.removeEventListener('focus', refreshPublishedDesigns);
  }, [configurationOpen]);

  const reportPreparationFailure = useCallback((format: DataTableOutputFormat, error: unknown) => {
    const copy = copyFor(options.language);
    setDownloadError(null);
    setIsOpen(true);
    setJob({
      id: null,
      format,
      status: 'FAILED',
      stage: 'FAILED',
      fileName: null,
      recordCount: 0,
      columnCount: options.columnCount ?? options.columns.length,
      redactedColumnCount: 0,
      archived: false,
      errorMessage: error instanceof Error && error.message ? error.message : copy.startError,
      createdAt: new Date().toISOString(),
      finishedAt: new Date().toISOString(),
      durationMs: null,
      downloadUrl: null,
    });
  }, [options.columnCount, options.columns.length, options.language]);

  const startExport = useCallback(async (
    format: DataTableOutputFormat,
    retry = false,
    requestOverride?: DataTableOutputExportRequest,
    selectionOverride?: { locale: SupportedLanguage; templateId: string },
  ) => {
    if (
      !options.enabled
      || readyCompanyScopeId !== outputCompanyScopeId
      || !readyFormats.includes(format)
      || isSubmitting
    ) return;
    if (job?.status === 'QUEUED' || job?.status === 'RUNNING' || job?.status === 'RETRYING') {
      setIsOpen(true);
      return;
    }
    let request: DataTableOutputExportRequest;
    try {
      request = (retry ? lastRequestRef.current : null)
        ?? requestOverride
        ?? options.prepareRequest?.(options.scope)
        ?? options;
    } catch (error) {
      reportPreparationFailure(format, error);
      return;
    }
    if (request.rows.length === 0 || request.columns.length === 0) return;
    if (!retry) lastServerPreparedRef.current = null;
    lastRequestRef.current = request;
    const selection = retry
      ? (lastSelectionRef.current ?? selectionOverride ?? { locale: options.language, templateId: '' })
      : (selectionOverride ?? { locale: options.language, templateId: '' });
    lastSelectionRef.current = selection;
    const copy = copyFor(options.language);
    companyScopeRef.current = outputCompanyScopeId;
    const preparing: DataTableOutputExportJob = {
      id: null,
      format,
      status: 'PREPARING',
      stage: 'PREPARING',
      fileName: null,
      recordCount: request.rows.length,
      columnCount: request.columns.length,
      redactedColumnCount: 0,
      archived: false,
      errorMessage: null,
      createdAt: new Date().toISOString(),
      finishedAt: null,
      durationMs: null,
      downloadUrl: null,
    };
    setIsOpen(true);
    setIsSubmitting(true);
    setDownloadError(null);
    setJob(preparing);
    try {
      const response = await axios.post(
        '/api/output-exports/table-jobs',
        {
          schemaVersion: request.schemaVersion,
          format,
          title: pageTitle(request.title) || 'DataTable',
          timeZone: request.timeZone,
          dateFormat: request.dateFormat,
          timeFormat: request.timeFormat,
          fileName: request.fileName,
          tableId: request.tableId,
          pagePath: pagePath(),
          locale: selection.locale,
          templateId: selection.templateId || undefined,
          scope: request.scope,
          columns: request.columns,
          rows: request.rows,
          filters: request.filters,
          quickSearch: request.quickSearch,
          filterSummary: request.filterSummary,
          sort: request.sort,
          retry,
        },
        { headers: { [COMPANY_SCOPE_HEADER]: companyScopeRef.current } },
      );
      if (mountedRef.current) setJob(normalizeDataTableOutputJob(response.data, preparing));
    } catch (error) {
      if (mountedRef.current) {
        setJob({
          ...preparing,
          status: 'FAILED',
          stage: 'FAILED',
          errorMessage: apiErrorMessage(error, copy.startError),
        });
      }
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  }, [isSubmitting, job?.status, options, outputCompanyScopeId, readyCompanyScopeId, readyFormats, reportPreparationFailure]);

  const startServerPreparedExport = useCallback(async (
    format: DataTableOutputFormat,
    createJob: (retry: boolean) => Promise<unknown>,
    retry = false,
  ) => {
    if (
      !options.enabled
      || readyCompanyScopeId !== outputCompanyScopeId
      || !readyFormats.includes(format)
      || isSubmitting
    ) return;
    if (job?.status === 'QUEUED' || job?.status === 'RUNNING' || job?.status === 'RETRYING') {
      setIsOpen(true);
      return;
    }
    const copy = copyFor(options.language);
    const preparing: DataTableOutputExportJob = {
      id: null,
      format,
      status: 'PREPARING',
      stage: 'PREPARING',
      fileName: null,
      recordCount: 0,
      columnCount: options.columnCount ?? options.columns.length,
      redactedColumnCount: 0,
      archived: false,
      errorMessage: null,
      createdAt: new Date().toISOString(),
      finishedAt: null,
      durationMs: null,
      downloadUrl: null,
    };
    companyScopeRef.current = outputCompanyScopeId;
    lastRequestRef.current = null;
    lastServerPreparedRef.current = { format, createJob };
    setIsOpen(true);
    setIsSubmitting(true);
    setDownloadError(null);
    setJob(preparing);
    try {
      const response = await createJob(retry);
      const queued = normalizeDataTableOutputJob(response, preparing);
      if (!queued.id || queued.format !== format || queued.status === 'PREPARING') {
        throw new Error(copy.startError);
      }
      if (mountedRef.current) setJob(queued);
    } catch (error) {
      if (mountedRef.current) {
        setJob({
          ...preparing,
          status: 'FAILED',
          stage: 'FAILED',
          errorMessage: apiErrorMessage(error, copy.startError),
        });
      }
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  }, [isSubmitting, job?.status, options, outputCompanyScopeId, readyCompanyScopeId, readyFormats]);

  const submitConfiguration = useCallback(async () => {
    if (configurationActionBusy || isSubmitting) return;
    const descriptor = outputOptions?.formats.find((format) => format.code === configurationFormat);
    const template = outputOptions?.templates.find((item) => item.id === configurationTemplateId);
    if (!descriptor?.available || !template?.formats.includes(configurationFormat)) return;
    setConfigurationActionBusy(true);
    setOptionsLoadError(null);
    try {
      // Validate before changing defaults or starting a provider operation. A
      // malformed cell must leave the list and the configuration usable.
      const request = configurationScope === 'ALL_FILTERED_ROWS' ? null
        : (configurationScope === 'SELECTED_ROWS' ? configurationSelectedRequest : null)
          ?? options.prepareRequest?.(configurationScope)
          ?? (configurationScope === 'SELECTED_ROWS' ? options.selectedRequest : options.visibleRequest ?? options);
      if (configurationScope !== 'ALL_FILTERED_ROWS' && !request) {
        throw new Error('The selected output scope is not available.');
      }
      const headers = { [COMPANY_SCOPE_HEADER]: outputCompanyScopeId };
      const systemManagedAllFiltered = configurationScope === 'ALL_FILTERED_ROWS'
        && options.allFilteredTemplatePolicy === 'SYSTEM_MANAGED';
      if (saveUserDefault && !systemManagedAllFiltered) {
        await axios.put('/api/output-exports/template-default', {
          subjectType: 'USER',
          format: configurationFormat,
          locale: configurationLocale,
          templateCode: template.code,
        }, { headers });
      }
      if (saveCompanyDefault && options.canSetCompanyDefault && !systemManagedAllFiltered) {
        await axios.put('/api/output-exports/template-default', {
          subjectType: 'COMPANY',
          format: configurationFormat,
          locale: configurationLocale,
          templateCode: template.code,
        }, { headers });
      }
      setConfigurationOpen(false);
      if (configurationScope === 'ALL_FILTERED_ROWS') {
        if (!options.queueAllFilteredOutput) throw new Error('ALL_FILTERED_ROWS provider is not available.');
        await startServerPreparedExport(configurationFormat, (retry) => options.queueAllFilteredOutput!({
          format: configurationFormat,
          locale: configurationLocale,
          templateId: template.id,
          retry,
        }));
      } else {
        if (!request) throw new Error('The selected output scope is not available.');
        await startExport(configurationFormat, false, { ...request, scope: configurationScope }, {
          locale: configurationLocale,
          templateId: template.id,
        });
      }
    } catch (error) {
      setConfigurationOpen(true);
      setOptionsLoadError(apiErrorMessage(error, copyFor(options.language).startError));
    } finally {
      setConfigurationActionBusy(false);
    }
  }, [
    configurationActionBusy,
    configurationFormat,
    configurationLocale,
    configurationScope,
    configurationSelectedRequest,
    configurationTemplateId,
    isSubmitting,
    options,
    outputOptions,
    saveCompanyDefault,
    saveUserDefault,
    outputCompanyScopeId,
    startExport,
    startServerPreparedExport,
  ]);

  const openCorporateOutput = useCallback(() => {
    if (!options.enabled) return;
    if (job?.status === 'QUEUED' || job?.status === 'RUNNING' || job?.status === 'RETRYING') {
      setIsOpen(true);
      return;
    }
    setConfigurationSelectedRequest(options.selectedRequest ?? null);
    setConfigurationScope(
      options.defaultScope === 'ALL_FILTERED_ROWS' && options.queueAllFilteredOutput
        ? 'ALL_FILTERED_ROWS'
        : options.hasSelectedRows || options.selectedRequest ? 'SELECTED_ROWS' : 'VISIBLE_ROWS',
    );
    setConfigurationLocale(options.language);
    setConfigurationOpen(true);
  }, [job?.status, options.defaultScope, options.enabled, options.hasSelectedRows, options.language, options.queueAllFilteredOutput, options.selectedRequest]);

  const openCorporateOutputForRequest = useCallback((request: DataTableOutputExportRequest) => {
    if (!options.enabled || request.rows.length === 0 || request.columns.length === 0) return;
    if (job?.status === 'QUEUED' || job?.status === 'RUNNING' || job?.status === 'RETRYING') {
      setIsOpen(true);
      return;
    }
    setConfigurationSelectedRequest(request);
    setConfigurationScope('SELECTED_ROWS');
    setConfigurationLocale(options.language);
    setConfigurationOpen(true);
  }, [job?.status, options.enabled, options.language]);

  useEffect(() => {
    const active = job?.status === 'QUEUED' || job?.status === 'RUNNING' || job?.status === 'RETRYING';
    if (!job?.id || !active) return undefined;
    let cancelled = false;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const response = await axios.get(
          `/api/output-exports/table-jobs/${encodeURIComponent(job.id!)}`,
          { headers: { [COMPANY_SCOPE_HEADER]: companyScopeRef.current } },
        );
        if (cancelled) return;
        const next = normalizeDataTableOutputJob(response.data, job);
        setJob(next);
        if (next.status === 'QUEUED' || next.status === 'RUNNING' || next.status === 'RETRYING') {
          timer = window.setTimeout(poll, 1_800);
        }
      } catch {
        if (!cancelled) timer = window.setTimeout(poll, 3_000);
      }
    };
    timer = window.setTimeout(poll, 1_200);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [job]);

  const download = useCallback(async () => {
    if (!job?.downloadUrl || isDownloading) return;
    const copy = copyFor(options.language);
    setIsDownloading(true);
    setDownloadError(null);
    try {
      const response = await axios.get(job.downloadUrl, {
        responseType: 'arraybuffer',
        headers: { [COMPANY_SCOPE_HEADER]: companyScopeRef.current },
      });
      const buffer = validateOutputResponse(response.data, response.headers['content-type'], job.format);
      const blob = new Blob([buffer], { type: OUTPUT_MIME_TYPES[job.format] });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = responseFileName(
        response.headers['content-disposition'],
        job.fileName || `table-export.${OUTPUT_EXTENSIONS[job.format]}`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
    } catch (error) {
      setDownloadError(apiErrorMessage(error, copy.downloadError));
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading, job, options.language]);

  const viewPdf = useCallback(async () => {
    if (!job?.downloadUrl || job.format !== 'PDF' || isDownloading || isViewing) return;
    const copy = copyFor(options.language);
    const mobileWebView = isQubeProMobileWebView();
    const previewWindow = mobileWebView ? null : window.open('about:blank', '_blank');
    if (!mobileWebView && !previewWindow) {
      setDownloadError(copy.viewError);
      return;
    }
    if (previewWindow) {
      previewWindow.opener = null;
      previewWindow.document.title = copy.viewing;
    }
    setIsViewing(true);
    setDownloadError(null);
    try {
      const response = await axios.get(job.downloadUrl, {
        responseType: 'arraybuffer',
        headers: { [COMPANY_SCOPE_HEADER]: companyScopeRef.current },
      });
      const buffer = validateOutputResponse(response.data, response.headers['content-type'], 'PDF');
      const url = URL.createObjectURL(new Blob([buffer], { type: OUTPUT_MIME_TYPES.PDF }));
      if (mobileWebView) {
        window.location.assign(url);
      } else {
        previewWindow!.location.replace(url);
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 300_000);
    } catch (error) {
      previewWindow?.close();
      setDownloadError(apiErrorMessage(error, copy.viewError));
    } finally {
      setIsViewing(false);
    }
  }, [isDownloading, isViewing, job, options.language]);

  return {
    isBusy: isSubmitting || job?.status === 'QUEUED' || job?.status === 'RUNNING' || job?.status === 'RETRYING',
    openCorporateOutput,
    openCorporateOutputForRequest,
    startExport,
    startServerPreparedExport,
    reportPreparationFailure,
    isFormatReady: (format: DataTableOutputFormat) => (
      readyCompanyScopeId === outputCompanyScopeId && readyFormats.includes(format)
    ),
    configurationDialogProps: {
      isOpen: configurationOpen,
      language: options.language,
      loading: optionsLoading,
      submitting: configurationActionBusy || isSubmitting,
      options: outputOptions,
      loadError: optionsLoadError,
      scope: configurationScope,
      format: configurationFormat,
      locale: configurationLocale,
      templateId: configurationTemplateId,
      saveUserDefault,
      saveCompanyDefault,
      selectedRowsAvailable: Boolean(configurationSelectedRequest ?? options.selectedRequest) || Boolean(options.hasSelectedRows),
      allFilteredAvailable: Boolean(options.queueAllFilteredOutput),
      canSetCompanyDefault: Boolean(options.canSetCompanyDefault),
      canCloneTemplate: Boolean(options.canCloneTemplate),
      designCatalogUrl: outputDesignCatalogUrl(),
      returnedDesignPending,
      systemManagedAllFiltered: configurationScope === 'ALL_FILTERED_ROWS'
        && options.allFilteredTemplatePolicy === 'SYSTEM_MANAGED',
      onClose: () => {
        setConfigurationOpen(false);
        setConfigurationSelectedRequest(null);
      },
      onRefresh: () => setOptionsRefreshKey((value) => value + 1),
      onScopeChange: setConfigurationScope,
      onFormatChange: setConfigurationFormat,
      onLocaleChange: setConfigurationLocale,
      onTemplateChange: (templateId: string) => {
        setConfigurationTemplateId(templateId);
      },
      onSaveUserDefaultChange: setSaveUserDefault,
      onSaveCompanyDefaultChange: setSaveCompanyDefault,
      onSubmit: () => { void submitConfiguration(); },
      onDesignCatalogOpen: () => {
        refreshDesignCatalogOnFocusRef.current = true;
      },
    } satisfies DataTableCorporateOutputDialogProps,
    dialogProps: {
      isOpen,
      job,
      language: options.language,
      downloadError,
      isDownloading,
      isViewing,
      onClose: () => setIsOpen(false),
      onDownload: () => { void download(); },
      onView: () => { void viewPdf(); },
      onRetry: () => {
        if (!job) return;
        const serverPrepared = lastServerPreparedRef.current;
        if (serverPrepared?.format === job.format) {
          void startServerPreparedExport(job.format, serverPrepared.createJob, true);
        } else {
          void startExport(job.format, true);
        }
      },
      onCreateNew: () => {
        if (!job) return;
        const serverPrepared = lastServerPreparedRef.current;
        if (serverPrepared?.format === job.format) {
          void startServerPreparedExport(job.format, serverPrepared.createJob, true);
        } else {
          void startExport(job.format, true);
        }
      },
    } satisfies DataTableOutputExportDialogProps,
  };
}

function stageIndex(stage: DataTableOutputExportStage): number {
  if (stage === 'PREPARING') return 0;
  if (stage === 'QUEUED' || stage === 'RENDERING') return 1;
  if (stage === 'ARCHIVING') return 2;
  return 3;
}

function formatDateTime(value: string | null, language: SupportedLanguage): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(language);
}

function formatDuration(value: number | null, language: SupportedLanguage): string {
  if (value == null || !Number.isFinite(value)) return '-';
  if (value < 1_000) return `${Math.max(0, Math.round(value))} ms`;
  return `${new Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(value / 1_000)} s`;
}

function FormatIcon({ format }: { format: DataTableOutputFormat }) {
  return format === 'XLSX' ? <FileSpreadsheet size={24} /> : <FileText size={24} />;
}

export function DataTableCorporateOutputDialog({
  isOpen,
  language,
  loading,
  submitting,
  options,
  loadError,
  scope,
  format,
  locale,
  templateId,
  saveUserDefault,
  saveCompanyDefault,
  selectedRowsAvailable,
  allFilteredAvailable,
  canSetCompanyDefault,
  canCloneTemplate,
  designCatalogUrl,
  returnedDesignPending,
  systemManagedAllFiltered,
  onClose,
  onRefresh,
  onScopeChange,
  onFormatChange,
  onLocaleChange,
  onTemplateChange,
  onSaveUserDefaultChange,
  onSaveCompanyDefaultChange,
  onSubmit,
  onDesignCatalogOpen,
}: DataTableCorporateOutputDialogProps) {
  const tr = language === 'tr';
  const translations = useTranslation(language);
  const languageNames = (translations.common?.languageNames || {}) as Partial<Record<SupportedLanguage, string>>;
  const languageDisplayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([language], { type: 'language' });
    } catch {
      return null;
    }
  }, [language]);
  const outputLocaleOptions = SUPPORTED_LANGUAGES.map((item) => ({
    value: item,
    label: languageDisplayNames?.of(item) || languageNames[item] || NATIVE_LANGUAGE_NAMES[item],
  }));
  const generatedFormId = useId();
  const formId = `datatable-corporate-output-${generatedFormId.replace(/:/g, '')}`;
  const descriptor = options?.formats.find((item) => item.code === format);
  const templates = options?.templates.filter((template) => template.formats.includes(format)) || [];
  const selectedTemplate = templates.find((template) => template.id === templateId);
  const canSubmit = Boolean(descriptor?.available && selectedTemplate && !loading && !submitting);
  const reason = outputReasonText(descriptor?.reasonCode, language);
  const declaredFormats = options?.formats || [];
  const unavailableFormats = options?.formats.filter((item) => !item.available) || [];
  const formFields: FormField[] = [
    {
      name: 'scope',
      label: tr ? 'Çıktıya alınacak kayıtlar' : 'Records to include',
      type: 'select',
      options: [
        ...(selectedRowsAvailable
          ? [{ value: 'SELECTED_ROWS', label: tr ? 'Seçili kayıtlar' : 'Selected records' }]
          : []),
        { value: 'VISIBLE_ROWS', label: tr ? 'Bu sayfadaki kayıtlar' : 'Records on this page' },
        ...(allFilteredAvailable
          ? [{ value: 'ALL_FILTERED_ROWS', label: tr ? 'Tüm filtrelenmiş kayıtlar' : 'All filtered records' }]
          : []),
      ],
      description: tr
        ? 'Çıktıda hangi kayıtların yer alacağını belirler. Seçim yaptıysanız seçili kayıtları; aksi halde bu sayfadaki kayıtları alabilirsiniz. Tüm filtrelenmiş kayıtlar yalnız yetkili sunucu desteği olan tablolarda görünür.'
        : 'Controls which records are included. Export selected records when a selection exists, or the records on this page. All filtered records appears only for tables with an authorized server provider.',
      disabled: submitting,
      colSpan: 6,
    },
    {
      name: 'locale',
      label: tr ? 'Çıktı dili' : 'Output language',
      type: 'select',
      options: outputLocaleOptions,
      disabled: submitting,
      colSpan: 6,
    },
    {
      name: 'format',
      label: 'Format',
      type: 'select',
      options: declaredFormats.map((item) => ({
        value: item.code,
        label: item.available
          ? `${item.code} · ${tr ? 'Hazır' : 'Ready'}`
          : `${item.code} · ${outputReasonText(item.reasonCode, language) || (tr ? 'Hazır değil' : 'Not ready')}`,
        disabled: !item.available,
      })),
      description: tr
        ? 'Hazır olmayan formatlar nedeni ile birlikte gösterilir; üretim için seçilemez.'
        : 'Formats that are not ready remain visible with their reason and cannot be selected for generation.',
      disabled: submitting || loading || declaredFormats.length === 0,
      colSpan: 6,
    },
    {
      name: 'templateId',
      label: tr ? 'Yayınlanmış tasarım' : 'Published design',
      type: 'select',
      options: templates.map((template) => ({
        value: template.id,
        label: `${template.name} · ${template.ownership === 'SYSTEM' ? (tr ? 'QubePro sistem' : 'QubePro system') : (tr ? 'Firma' : 'Company')} · v${template.version}`,
      })),
      description: tr
        ? systemManagedAllFiltered
          ? 'Tüm filtrelenmiş büyük veri çıktısı, güvenli ve kilitli QubePro sistem tasarımını kullanır.'
          : 'Yalnız yayınlanmış sistem ve firma tasarımları listelenir.'
        : systemManagedAllFiltered
          ? 'The full filtered large-data export uses the secure locked QubePro system design.'
          : 'Only published system and company designs are listed.',
      disabled: systemManagedAllFiltered || submitting || loading || templates.length === 0,
      colSpan: 6,
    },
  ];

  formFields.push({
    name: 'saveUserDefault',
    label: tr ? 'Seçimimi kişisel varsayılan yap' : 'Make this my default',
    type: 'checkbox',
    disabled: systemManagedAllFiltered || submitting,
    colSpan: canSetCompanyDefault ? 6 : 12,
  });
  if (canSetCompanyDefault) {
    formFields.push({
      name: 'saveCompanyDefault',
      label: tr ? 'Firma varsayılanı yap' : 'Make company default',
      type: 'checkbox',
      disabled: systemManagedAllFiltered || submitting,
      colSpan: 6,
    });
  }

  const formValues = {
    scope,
    locale,
    format,
    templateId,
    saveUserDefault,
    saveCompanyDefault,
  };

  const handleFormChange = (next: Record<string, unknown>) => {
    if (
      next.scope !== scope
      && (next.scope === 'VISIBLE_ROWS' || next.scope === 'SELECTED_ROWS' || next.scope === 'ALL_FILTERED_ROWS')
    ) {
      onScopeChange(next.scope);
    }
    if (
      next.locale !== locale
      && typeof next.locale === 'string'
      && SUPPORTED_LANGUAGES.includes(next.locale as SupportedLanguage)
    ) {
      onLocaleChange(next.locale as SupportedLanguage);
    }
    if (
      next.format !== format
      && (next.format === 'PDF' || next.format === 'XLSX' || next.format === 'CSV')
    ) {
      onFormatChange(next.format);
    }
    if (next.templateId !== templateId && typeof next.templateId === 'string') {
      onTemplateChange(next.templateId);
    }
    if (next.saveUserDefault !== saveUserDefault && typeof next.saveUserDefault === 'boolean') {
      onSaveUserDefaultChange(next.saveUserDefault);
    }
    if (next.saveCompanyDefault !== saveCompanyDefault && typeof next.saveCompanyDefault === 'boolean') {
      onSaveCompanyDefaultChange(next.saveCompanyDefault);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={tr ? 'Kurumsal çıktı oluştur' : 'Create corporate output'}
      size="md"
      maximizable={false}
      closeOnOverlayClick={!submitting}
      footer={(
        <div className="output-export-job-dialog__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
            {tr ? 'Vazgeç' : 'Cancel'}
          </button>
          <button type="submit" form={formId} className="btn-primary" disabled={!canSubmit}>
            {submitting ? <Loader2 size={17} className="output-export-job-dialog__spin" /> : <Download size={17} />}
            <span>{tr ? 'Çıktıyı oluştur' : 'Create output'}</span>
          </button>
        </div>
      )}
    >
      <div className="datatable-corporate-output" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="datatable-corporate-output__intro">
          <span className="datatable-corporate-output__intro-icon"><ShieldCheck size={22} /></span>
          <div>
            <strong>{tr ? 'QubePro güvenli çıktı zinciri' : 'QubePro secure output chain'}</strong>
            <span>{tr
              ? 'Tablo verisi ürün API’sinden Output kuyruğuna alınır, Worker tarafından üretilir ve DMS’e arşivlenir.'
              : 'Table data is queued through the product API, rendered by the Worker, and archived in DMS.'}</span>
          </div>
        </div>

        {loadError && (
          <div className="datatable-corporate-output__notice is-error" role="alert">
            <AlertTriangle size={18} />
            <span>{loadError}</span>
            <button type="button" className="btn-secondary" onClick={onRefresh}>{tr ? 'Yenile' : 'Refresh'}</button>
          </div>
        )}

        {returnedDesignPending && (
          <div className="datatable-corporate-output__notice" role="status">
            <AlertTriangle size={18} />
            <span>{tr
              ? 'Tasarım taslak durumda. Yayınlanmadan üretimde seçilemez; katalog yalnız yayınlanmış tasarımları gösterir.'
              : 'The design is still a draft. It cannot be selected until published; the catalog lists published designs only.'}</span>
          </div>
        )}

        {systemManagedAllFiltered && (
          <div className="datatable-corporate-output__notice" role="status">
            <ShieldCheck size={18} />
            <span>{tr
              ? 'Tüm filtrelenmiş kayıtlar sunucuda sabitlenir ve tek dosya için kilitli sistem tasarımıyla işlenir.'
              : 'All filtered records are frozen on the server and rendered as one file with the locked system design.'}</span>
          </div>
        )}

        <form
          id={formId}
          className="datatable-corporate-output__form"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) onSubmit();
          }}
          noValidate
        >
          <FormBuilder
            fields={formFields}
            values={formValues}
            onChange={handleFormChange}
            language={language}
            errors={{}}
          />
          {format === 'CSV' && (
            <p className="datatable-corporate-output__hint">{tr ? 'CSV görsel tasarım taşımaz ve QubePro standardıyla üretilir.' : 'CSV has no visual design and uses the QubePro standard.'}</p>
          )}
          {canCloneTemplate && format !== 'CSV' && !systemManagedAllFiltered && (
            <div className="datatable-corporate-output__design-management">
              <div>
                <strong>{tr ? 'Aradığınız tasarım listede yok mu?' : 'Can’t find the design you need?'}</strong>
                <span>{tr
                  ? 'Kopyalama, düzenleme ve yayınlama işlemlerini Output tasarım kataloğunda yönetin. Bu pencereye döndüğünüzde yayınlanmış tasarımlar otomatik yenilenir.'
                  : 'Manage cloning, editing, and publishing in the Output design catalog. Published designs refresh automatically when you return to this window.'}</span>
              </div>
              <a
                className="btn-secondary"
                href={designCatalogUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onDesignCatalogOpen}
              >
                <ExternalLink size={16} /> {tr ? 'Tasarım kataloğunu aç' : 'Open design catalog'}
              </a>
            </div>
          )}
        </form>

        {!loading && options && options.formats.length === 0 && (
          <div className="datatable-corporate-output__notice"><AlertTriangle size={18} />{tr ? 'DataTable ailesi için format ilan edilmedi.' : 'No formats are declared for the DataTable family.'}</div>
        )}
        {unavailableFormats.length > 0 && (
          <div className="datatable-corporate-output__notice" role="status">
            <AlertTriangle size={18} />
            <span>{unavailableFormats.map((item) => `${item.code}: ${outputReasonText(item.reasonCode, language)}`).join(' · ')}</span>
          </div>
        )}
        {reason && unavailableFormats.length === 0 && <div className="datatable-corporate-output__notice"><AlertTriangle size={18} /><span>{reason}</span></div>}

        {loading && <div className="datatable-corporate-output__loading"><Loader2 size={18} className="output-export-job-dialog__spin" /> {tr ? 'Output seçenekleri yükleniyor…' : 'Loading Output options…'}</div>}
      </div>
    </FormModal>
  );
}

export function DataTableOutputExportDialog({
  isOpen,
  job,
  language,
  downloadError,
  isDownloading,
  isViewing,
  onClose,
  onDownload,
  onView,
  onRetry,
  onCreateNew,
}: DataTableOutputExportDialogProps) {
  const copy = copyFor(language);
  const format = job?.format ?? 'PDF';
  const withFormat = (value: string) => interpolate(value, { format });
  const stage = job?.stage ?? 'PREPARING';
  const currentStage = stageIndex(stage);
  const failed = job?.status === 'FAILED' || job?.status === 'DEAD' || job?.status === 'CANCELLED' || stage === 'FAILED';
  const ready = job?.status === 'SUCCESS' && stage === 'READY';
  const steps = [copy.preparing, withFormat(copy.generating), copy.archiving, copy.ready];
  const statusTitle = failed
    ? withFormat(copy.failedTitle)
    : ready
      ? withFormat(copy.readyTitle)
      : stage === 'QUEUED'
        ? copy.queuedTitle
        : steps[currentStage];

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={withFormat(copy.title)}
      size="md"
      maximizable={false}
      contentClassName="output-export-workflow-modal"
      footer={(
        <div className="output-export-job-dialog__actions">
          <div className="output-export-job-dialog__actions-start">
            {ready && (
              <button
                type="button"
                className="qubepro-form-action btn-secondary output-export-job-dialog__new-file"
                onClick={onCreateNew}
              >
                <Plus size={16} />
                <span>{withFormat(copy.newFile)}</span>
              </button>
            )}
          </div>
          <div className="output-export-job-dialog__actions-end">
            <button type="button" className="qubepro-form-action btn-secondary" onClick={onClose}>{copy.close}</button>
            {failed && (
              <button type="button" className="qubepro-form-action btn-primary" onClick={onRetry}>
                <RefreshCw size={17} />
                <span>{copy.retry}</span>
              </button>
            )}
            {ready && job?.downloadUrl && (
              <>
                {job.format === 'PDF' && (
                  <button
                    type="button"
                    className="qubepro-form-action btn-secondary"
                    onClick={onView}
                    disabled={isViewing || isDownloading}
                  >
                    {isViewing
                      ? <Loader2 size={17} className="output-export-job-dialog__spin" />
                      : <Eye size={17} />}
                    <span>{isViewing ? copy.viewing : withFormat(copy.view)}</span>
                  </button>
                )}
                <button
                  type="button"
                  className="qubepro-form-action btn-primary"
                  onClick={onDownload}
                  disabled={isDownloading || isViewing}
                >
                  {isDownloading
                    ? <Loader2 size={17} className="output-export-job-dialog__spin" />
                    : <Download size={17} />}
                  <span>{isDownloading ? copy.downloading : withFormat(copy.download)}</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    >
      <div className="output-export-job-dialog" aria-live="polite">
        <div className={`output-export-job-dialog__status${failed ? ' is-failed' : ready ? ' is-ready' : ''}`}>
          <span className="output-export-job-dialog__status-icon" aria-hidden="true">
            {failed ? <XCircle size={24} /> : ready ? <CheckCircle2 size={24} /> : <FormatIcon format={format} />}
          </span>
          <div className="output-export-job-dialog__status-copy">
            <strong>{statusTitle}</strong>
            <span>{failed ? (job?.errorMessage || copy.failedMessage) : ready ? copy.readyMessage : copy.activeMessage}</span>
          </div>
        </div>

        <ol className="output-export-job-dialog__steps" aria-label={copy.stagesAria}>
          {steps.map((label, index) => {
            const complete = ready || (!failed && index < currentStage);
            const current = !ready && !failed && index === currentStage;
            return (
              <li key={label} className={`${complete ? 'is-complete' : ''}${current ? ' is-current' : ''}`.trim()}>
                <span className="output-export-job-dialog__step-icon" aria-hidden="true">
                  {complete ? <Check size={15} /> : current ? <Loader2 size={15} className="output-export-job-dialog__spin" /> : <span>{index + 1}</span>}
                </span>
                <span>{label}</span>
              </li>
            );
          })}
        </ol>

        <dl className="output-export-job-dialog__details">
          <div><dt>{copy.file}</dt><dd title={job?.fileName || undefined}>{job?.fileName || copy.beingGenerated}</dd></div>
          <div><dt>{copy.records}</dt><dd>{job?.recordCount ?? 0}</dd></div>
          <div><dt>{copy.columns}</dt><dd>{job?.columnCount ?? 0}</dd></div>
          <div><dt>{copy.jobId}</dt><dd>{job?.id || '-'}</dd></div>
          <div><dt>{copy.started}</dt><dd>{formatDateTime(job?.createdAt ?? null, language)}</dd></div>
          {ready && <div><dt>{copy.duration}</dt><dd>{formatDuration(job?.durationMs ?? null, language)}</dd></div>}
        </dl>

        {Boolean(job?.redactedColumnCount) && (
          <div className="output-export-job-dialog__security-note">
            <ShieldCheck size={17} />
            <span>{interpolate(copy.sensitiveExcluded, { count: job!.redactedColumnCount })}</span>
          </div>
        )}

        {ready && downloadError && (
          <div className="output-export-job-dialog__download-error" role="alert">
            <AlertTriangle size={17} /><span>{downloadError}</span>
          </div>
        )}
      </div>
    </FormModal>
  );
}
